import React, { Fragment, useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";

import { notBlank } from "../util/ext";
import Grid from "@material-ui/core/Grid";
import OrganizationLogoCard from "./cards/OrganizationLogoCard";
import OrganizationLocationCard from "./cards/OrganizationLocationCard";
import OrganizationRolesCard from "./cards/OrganizationRolesCard";
import OrganizationAssociationsCard from "./cards/OrganizationAssociationsCard";
import OrganizationContentProviderCard from "./cards/OrganizationContentProviderCard";
import OrganizationDetailsCard from "./cards/OrganizationDetailsCard";
import OrganizationProfileCard from "./cards/OrganizationProfileCard";
import OrganizationSubscriptionsCard from "./cards/OrganizationSubscriptionsCard";
import OrganizationAuthenticationCard from "./cards/OrganizationAuthenticationCard";
import OrganizationPropertiesCard from "./cards/OrganizationPropertiesCard";
import OrganizationUsersCard from "./cards/OrganizationUsersCard";
import OrganizationApplicationsCard from "./cards/OrganizationApplicationsCard";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useOrganizationDetails } from "./organizationServices";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import { Box } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import Header from "../creator-application/common/Header";
import OrganizationKeystoreCard from "./cards/OrganizationKeystoreCard";
import BuyingGroupMembersCard from "./cards/BuyingGroupMembersCard";
import LibrarySalesAccountCard from "./cards/LibrarySalesAccountCard";

const useStyles = makeStyles(theme => ({
  root: {
    border: "3px white solid",
    backgroundColor: "white",
    borderRadius: "20px",
  },

  panel: {
    backgroundColor: "white",
    paddingTop: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    maxHeight: "calc(100vh - 300px)",
    overflowY: "auto",
  },
}));

const OrganizationView = () => {
  const classes = useStyles();
  const history = useHistory();
  const { state = {} } = useLocation();
  const { organizationId } = useParams();
  const [organization, setOrganization] = useState(null);
  const [organizationLogoUrl, setOrganizationLogoUrl] = useState(null);
  const handleProfileLoaded = useCallback(profile => {
    setOrganizationLogoUrl(profile.brandingLogoUrl);
  }, []);

  const orgResponse = useOrganizationDetails(organizationId);
  useEffect(() => {
    if (orgResponse.isSuccess && notBlank(orgResponse.data)) {
      setOrganization(orgResponse.data);
    }
  }, [orgResponse.isSuccess, orgResponse.data]);

  return (
    <Container id="organization-view" classes={{ root: classes.root }}>
      {orgResponse.isLoading && (
        <Container style={{ height: 400 }}>
          <PanelLoadingIndicator />
        </Container>
      )}
      {orgResponse.isSuccess && notBlank(organization) && (
        <Fragment>
          <Header
            title={`${organization.organizationName} (${organization.organizationId})`}
            selectedActions={
              <Box>
                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => {
                    const newState = {
                      previousLocation: `/manage-organizations/organizations/${organizationId}`,
                      search:
                        notBlank(state) && notBlank(state.search)
                          ? state.search
                          : null,
                    };
                    notBlank(state) && notBlank(state.previousLocation)
                      ? history.push(state.previousLocation, newState)
                      : history.push(
                          `/manage-organizations/organizations`,
                          newState,
                        );
                  }}
                >
                  {notBlank(state) && notBlank(state.label)
                    ? state.label
                    : "Back to Browse"}
                </Button>
              </Box>
            }
          />
          <Container classes={{ root: classes.panel }}>
            <Grid container spacing={2}>
              <Grid item sm={12}>
                <Grid container spacing={2}>
                  <Grid item sm={3} container spacing={2}>
                    <Grid item sm={12}>
                      <OrganizationLogoCard
                        id={organization.organizationId}
                        url={organizationLogoUrl}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <OrganizationLocationCard
                        id={organization.organizationId}
                        organizationName={organization.organizationName}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <OrganizationRolesCard id={organization.organizationId} />
                    </Grid>
                  </Grid>

                  <Grid item sm={9} container spacing={2}>
                    <Grid item sm={12}>
                      <OrganizationDetailsCard
                        id={organization.organizationId}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <OrganizationProfileCard
                        id={organization.organizationId}
                        onProfileLoaded={handleProfileLoaded}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item sm={12}>
                <Grid container spacing={2}>
                  <Grid item sm={9} container spacing={2}>
                    <Grid item sm={12}>
                      <OrganizationSubscriptionsCard
                        id={organization.organizationId}
                      />
                    </Grid>
                  </Grid>

                  <Grid item sm={3} container spacing={2}>
                    <Grid item sm={12}>
                      <OrganizationAssociationsCard
                        id={organization.organizationId}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <OrganizationKeystoreCard
                        id={organization.organizationId}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <OrganizationContentProviderCard
                        id={organization.organizationId}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item sm={12} container spacing={3}>
                <Grid item sm={8} container spacing={3}>
                  <Grid item sm={12}>
                    <OrganizationAuthenticationCard
                      id={organization.organizationId}
                    />
                  </Grid>

                  {organization.library && (
                    <Grid item sm={12}>
                      <LibrarySalesAccountCard
                        id={organization.organizationId}
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid item sm={4}>
                  <OrganizationPropertiesCard
                    id={organization.organizationId}
                  />
                </Grid>
                <Grid item sm={7}>
                  <OrganizationUsersCard
                    id={organization.organizationId}
                    allowAdd={organization.userCreationAllowed}
                  />
                </Grid>
                <Grid item sm={5}>
                  <OrganizationApplicationsCard
                    id={organization.organizationId}
                  />
                </Grid>
                {organization.buyingGroup && (
                  <Grid item sm={12}>
                    <BuyingGroupMembersCard id={organization.organizationId} />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Container>
        </Fragment>
      )}
    </Container>
  );
};

export default OrganizationView;
